"use client";

import { useState, useEffect, useMemo } from "react";

const BASE_URL = process.env.NEXT_PUBLIC_SITE_URL || "https://forgeswap.fun";

const CHAIN_OPTIONS = [
  { value: "solana", label: "Solana" },
  { value: "ethereum", label: "Ethereum" },
  { value: "base", label: "Base" },
  { value: "bnb", label: "BNB Chain" },
  { value: "polygon", label: "Polygon" },
  { value: "arbitrum", label: "Arbitrum" },
] as const;

interface SpaceEvent {
  id: string;
  title: string;
  eventDate: string;
  description: string | null;
  slotDurationMinutes: number;
}

interface CurrentEventResponse {
  event: SpaceEvent | null;
  takenSlots: number[];
}

const initialForm = {
  project_name: "",
  token_symbol: "",
  token_contract_address: "",
  token_chain: "solana",
  twitter_handle: "",
  contact_email: "",
  telegram_handle: "",
  project_description: "",
  time_slot: "",
  website: "", // honeypot
};

function formatEventDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

export default function SpacesPage() {
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState<SpaceEvent | null>(null);
  const [takenSlots, setTakenSlots] = useState<number[]>([]);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedSlot, setSubmittedSlot] = useState<number | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchEvent() {
      try {
        const res = await fetch("/api/spaces/current-event");
        const data: CurrentEventResponse = await res.json();
        setEvent(data.event);
        setTakenSlots(data.takenSlots);
      } catch (e) {
        console.error("Failed to fetch event:", e);
      } finally {
        setLoading(false);
      }
    }
    fetchEvent();
  }, []);

  const update = (key: keyof typeof initialForm, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  // Generate slot options: show max(takenSlots) + 10, minimum 20
  const slotOptions = useMemo(() => {
    const maxTaken = takenSlots.length > 0 ? Math.max(...takenSlots) : 0;
    const totalSlots = Math.max(20, maxTaken + 10);
    return Array.from({ length: totalSlots }, (_, i) => i + 1);
  }, [takenSlots]);

  const handleSubmit = async () => {
    // Basic validation
    if (!form.project_name.trim()) {
      setError("Project name is required.");
      return;
    }
    if (!form.token_symbol.trim()) {
      setError("Token symbol is required.");
      return;
    }
    if (!form.twitter_handle.trim()) {
      setError("Twitter handle is required.");
      return;
    }
    if (!form.time_slot) {
      setError("Please select a time slot.");
      return;
    }

    const selectedSlot = parseInt(form.time_slot);
    if (takenSlots.includes(selectedSlot)) {
      setError("That slot is already taken. Please select another.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const res = await fetch("/api/spaces/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_name: form.project_name.trim(),
          token_symbol: form.token_symbol.trim().toUpperCase(),
          token_contract_address: form.token_contract_address.trim() || undefined,
          token_chain: form.token_chain,
          twitter_handle: form.twitter_handle.trim(),
          contact_email: form.contact_email.trim(),
          telegram_handle: form.telegram_handle.trim() || undefined,
          project_description: form.project_description.trim() || undefined,
          time_slot: selectedSlot,
          website: form.website, // honeypot
        }),
      });

      const data = await res.json();

      if (res.status === 429) {
        setError("Too many attempts. Please wait before trying again.");
        return;
      }

      if (!res.ok || !data.success) {
        setError(data.error || "Something went wrong.");
        return;
      }

      // Success
      setSubmittedSlot(data.timeSlot);
      setSubmitted(true);
      setForm(initialForm);
    } catch (e) {
      console.error(e);
      setError("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ background: "#111", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Inter, sans-serif" }}>
        <div style={{ color: "#888", fontSize: 16 }}>Loading...</div>
      </div>
    );
  }

  // No active event
  if (!event) {
    return (
      <div style={{ background: "#111", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Inter, sans-serif", padding: 24 }}>
        <div style={{ maxWidth: 560, textAlign: "center", padding: 40, background: "linear-gradient(180deg,rgba(22,22,26,0.95)_0%,rgba(16,16,20,0.95)_100%)", border: "1px solid rgba(255,255,255,0.07)", borderTop: "1px solid rgba(255,255,255,0.13)", borderRadius: 20, boxShadow: "0 8px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)" }}>
          <div style={{ fontSize: 64, marginBottom: 24 }}>🎙️</div>
          <h1 style={{ color: "#f9cd4f", fontSize: 28, fontWeight: 700, marginBottom: 16 }}>No Upcoming Spaces</h1>
          <p style={{ color: "#aaa", fontSize: 16, lineHeight: 1.7 }}>Check back soon for our next X Spaces event!</p>
          <a href={BASE_URL} style={{ display: "inline-block", marginTop: 32, background: "#f9cd4f", color: "#111", padding: "12px 32px", borderRadius: 10, fontWeight: 700, textDecoration: "none" }}>Back to ForgeSwap</a>
        </div>
      </div>
    );
  }

  // Success state
  if (submitted && submittedSlot) {
    return (
      <div style={{ background: "#111", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Inter, sans-serif", padding: 24 }}>
        <div style={{ maxWidth: 560, textAlign: "center", padding: 40, background: "linear-gradient(180deg,rgba(22,22,26,0.95)_0%,rgba(16,16,20,0.95)_100%)", border: "1px solid rgba(255,255,255,0.07)", borderTop: "1px solid rgba(255,255,255,0.13)", borderRadius: 20, boxShadow: "0 8px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)" }}>
          <div style={{ fontSize: 64, marginBottom: 24 }}>🎉</div>
          <h1 style={{ color: "#f9cd4f", fontSize: 28, fontWeight: 700, marginBottom: 16 }}>You're Signed Up!</h1>
          <p style={{ color: "#fff", fontSize: 18, lineHeight: 1.7, marginBottom: 8 }}>
            Slot {submittedSlot}
          </p>
          <p style={{ color: "#aaa", fontSize: 16, lineHeight: 1.7, marginBottom: 24 }}>
            See you on Saturday!
          </p>
          <div style={{ background: "rgba(249,205,79,0.12)", border: "1px solid rgba(249,205,79,0.3)", borderRadius: 12, padding: 16, marginBottom: 24 }}>
            <p style={{ color: "#f9cd4f", fontSize: 14, margin: 0 }}>
              Tip: Follow @ForgeSwapApp and @ForgeLaunchPad on X for the Space link
            </p>
          </div>
          <a href={BASE_URL} style={{ display: "inline-block", background: "#f9cd4f", color: "#111", padding: "12px 32px", borderRadius: 10, fontWeight: 700, textDecoration: "none" }}>Back to ForgeSwap</a>
        </div>
      </div>
    );
  }

  // Contract address placeholder based on chain
  const addressPlaceholder = form.token_chain === "solana"
    ? "e.g. So11111111111111111111111111111111111111112"
    : "e.g. 0x1234...abcd";

  return (
    <div style={{ background: "#111", minHeight: "100vh", color: "#fff", fontFamily: "Inter, sans-serif", overflow: "hidden", maxWidth: "100vw" }}>
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #1a1a1a, #111)", borderBottom: "1px solid #222", padding: "48px 16px 32px" }}>
        <div style={{ maxWidth: 840, margin: "0 auto", textAlign: "center" }}>
          <h1 style={{ color: "#f9cd4f", fontSize: "clamp(28px, 6vw, 42px)", fontWeight: 800, marginBottom: 16 }}>
            🎙️ {event.title}
          </h1>
          <p style={{ color: "#ccc", fontSize: 18, lineHeight: 1.7, maxWidth: 680, margin: "0 auto", marginBottom: 8 }}>
            {formatEventDate(event.eventDate)}
          </p>
          {event.description && (
            <p style={{ color: "#aaa", fontSize: 16, lineHeight: 1.7, maxWidth: 600, margin: "0 auto" }}>
              {event.description}
            </p>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 16px 48px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 32 }}>
        {/* Form Card */}
        <div style={{ background: "linear-gradient(180deg,rgba(22,22,26,0.95)_0%,rgba(16,16,20,0.95)_100%)", border: "1px solid rgba(255,255,255,0.07)", borderTop: "1px solid rgba(255,255,255,0.13)", borderRadius: 20, padding: 28, boxShadow: "0 8px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)" }}>
          <h2 style={{ color: "#f9cd4f", fontSize: 22, fontWeight: 700, margin: "0 0 8px" }}>Sign Up for a Slot</h2>
          <p style={{ color: "#888", fontSize: 14, margin: "0 0 28px" }}>
            Reserve your {event.slotDurationMinutes}-minute slot to shill your project live.
          </p>

          {error && (
            <div style={{ background: "#ef444422", border: "1px solid #ef4444", color: "#ef4444", padding: "12px 16px", borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
              {error}
            </div>
          )}

          <div style={{ display: "grid", gap: 16 }}>
            {/* Project Name */}
            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Project Name *</label>
              <input
                value={form.project_name}
                onChange={(e) => update("project_name", e.target.value)}
                placeholder="e.g. Forge Alpha"
                maxLength={100}
                style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }}
              />
            </div>

            {/* Token Symbol */}
            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Token Symbol *</label>
              <input
                value={form.token_symbol}
                onChange={(e) => update("token_symbol", e.target.value.toUpperCase())}
                placeholder="e.g. FORGE"
                maxLength={20}
                style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }}
              />
            </div>

            {/* Chain */}
            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Chain *</label>
              <select
                value={form.token_chain}
                onChange={(e) => update("token_chain", e.target.value)}
                style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14 }}
              >
                {CHAIN_OPTIONS.map((chain) => (
                  <option key={chain.value} value={chain.value}>{chain.label}</option>
                ))}
              </select>
            </div>

            {/* Token Contract Address (optional) */}
            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Token Contract Address (optional)</label>
              <input
                value={form.token_contract_address}
                onChange={(e) => update("token_contract_address", e.target.value)}
                placeholder={addressPlaceholder}
                style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }}
              />
            </div>

            {/* Twitter Handle */}
            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Twitter Handle *</label>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#666", fontSize: 14 }}>@</span>
                <input
                  value={form.twitter_handle}
                  onChange={(e) => update("twitter_handle", e.target.value.replace(/^@/, ""))}
                  placeholder="projecthandle"
                  maxLength={15}
                  style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px 12px 30px", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }}
                />
              </div>
            </div>

            {/* Contact Email (optional) */}
            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Email (optional)</label>
              <input
                type="email"
                value={form.contact_email}
                onChange={(e) => update("contact_email", e.target.value)}
                placeholder="team@project.com"
                style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }}
              />
            </div>

            {/* Telegram Handle (optional) */}
            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Telegram Handle (optional)</label>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#666", fontSize: 14 }}>@</span>
                <input
                  value={form.telegram_handle}
                  onChange={(e) => update("telegram_handle", e.target.value.replace(/^@/, ""))}
                  placeholder="projecttg"
                  maxLength={15}
                  style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px 12px 30px", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }}
                />
              </div>
            </div>

            {/* Project Description (optional) */}
            <div>
              <label style={{ display: "flex", justifyContent: "space-between", color: "#888", fontSize: 12, marginBottom: 6 }}>
                <span>Project Description (optional)</span>
                <span style={{ color: form.project_description.length > 280 ? "#ef4444" : "#666" }}>
                  {form.project_description.length}/280
                </span>
              </label>
              <textarea
                value={form.project_description}
                onChange={(e) => update("project_description", e.target.value.slice(0, 280))}
                placeholder="Brief description of your project (max 280 chars)"
                rows={3}
                style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14, boxSizing: "border-box", resize: "vertical" }}
              />
            </div>

            {/* Time Slot */}
            <div>
              <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Time Slot *</label>
              <select
                value={form.time_slot}
                onChange={(e) => update("time_slot", e.target.value)}
                style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14 }}
              >
                <option value="">Select a time slot...</option>
                {slotOptions.map((slot) => {
                  const isTaken = takenSlots.includes(slot);
                  return (
                    <option key={slot} value={slot} disabled={isTaken} style={{ color: isTaken ? "#666" : "#fff" }}>
                      Slot {slot}{isTaken ? " (Taken)" : ""}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Honeypot field - hidden from humans */}
            <div style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }} aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                value={form.website}
                onChange={(e) => update("website", e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                background: submitting ? "#886d2d" : "#f9cd4f",
                color: "#111",
                border: "none",
                padding: "14px 18px",
                borderRadius: 10,
                fontWeight: 700,
                cursor: submitting ? "not-allowed" : "pointer",
                fontSize: 16,
                marginTop: 8,
              }}
            >
              {submitting ? "Signing up..." : "Reserve My Slot"}
            </button>
          </div>
        </div>

        {/* Info Sidebar */}
        <div style={{ display: "grid", gap: 20, alignContent: "start" }}>
          <div style={{ background: "#1a1a1a", border: "1px solid #222", borderRadius: 16, padding: 24 }}>
            <h3 style={{ color: "#f9cd4f", fontSize: 18, fontWeight: 700, margin: "0 0 12px" }}>How It Works</h3>
            <div style={{ display: "grid", gap: 12 }}>
              {[
                "Pick an available time slot for your project's shill session.",
                `You get ${event.slotDurationMinutes} minutes to pitch live to the ForgeSwap community.`,
                "Follow @ForgeSwapApp on X for the Spaces link.",
                "Show up on time and make the most of your slot!",
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "#111", border: "1px solid #222", borderRadius: 12, padding: 14 }}>
                  <span style={{ color: "#f9cd4f", fontSize: 14, fontWeight: 700, minWidth: 20 }}>{i + 1}.</span>
                  <span style={{ color: "#ccc", fontSize: 14, lineHeight: 1.6 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: "#1a1a1a", border: "1px solid #222", borderRadius: 16, padding: 24 }}>
            <h3 style={{ color: "#f9cd4f", fontSize: 18, fontWeight: 700, margin: "0 0 12px" }}>What to Prepare</h3>
            <div style={{ display: "grid", gap: 10 }}>
              {[
                "A concise pitch for your project (keep it punchy!)",
                "Key stats: market cap, holders, recent milestones",
                "Your roadmap highlights and upcoming catalysts",
                "Links ready to share in chat",
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <span style={{ color: "#f9cd4f", fontSize: 14 }}>•</span>
                  <span style={{ color: "#ccc", fontSize: 14, lineHeight: 1.6 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: "rgba(249,205,79,0.08)", border: "1px solid rgba(249,205,79,0.25)", borderRadius: 16, padding: 20 }}>
            <p style={{ color: "#f9cd4f", fontSize: 14, margin: 0, lineHeight: 1.6 }}>
              <strong>Free to sign up!</strong> This is a community event. Just show up and shill responsibly.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
